import { useState } from "react";
import { Loader2, LogOut } from "lucide-react";
import { useAuthStore } from "../store/useAuthStore.ts";
import { changeOwnPassword, logout } from "../api/auth.ts";

// Rendered in place of the app whenever the signed-in user has
// mustChangePassword set, blocking navigation until they set a new password.
export default function ForceChangePasswordPage() {
  const { user, setUser } = useAuthStore();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await changeOwnPassword(user.id, password);
      setUser({ ...user, mustChangePassword: false });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    setUser(null);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md p-8 border rounded-lg bg-card shadow-sm">
        <div className="mb-6">
          <img src="/neuroxrehab-logo.svg" alt="neuroXRehab" className="h-10 w-auto mb-4" />
          <h1 className="font-semibold text-lg">Set a new password</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Your account was created with a temporary password, or an administrator just reset it.
            Choose a new password to continue.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1.5" htmlFor="new-password">
              New password
            </label>
            <input
              id="new-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              required
              autoComplete="new-password"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5" htmlFor="confirm-password">
              Confirm new password
            </label>
            <input
              id="confirm-password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-3 py-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              required
              autoComplete="new-password"
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Set new password
          </button>
        </form>

        <button
          onClick={handleLogout}
          className="w-full mt-4 py-2 px-4 text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-1.5"
        >
          <LogOut className="h-3.5 w-3.5" />
          Sign out instead
        </button>
      </div>
    </div>
  );
}
